import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AirVent, CheckCircle2, ArrowRight, Phone, Mail,
  ShieldCheck, Zap, Clock, Leaf, MapPin, ChevronRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import './Servicepage.css';

import imgPompe from '../assets/pompeService.jpg';
import logoRGE from '../assets/logo-qualipac-RGE.PNG';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Installation Pompe à Chaleur Île-de-France",
  "provider": {
    "@type": "LocalBusiness",
    "name": "SOCLIMATE",
    "telephone": "+33618011895",
    "email": "soclimate.95@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1 rue de Belloy",
      "addressLocality": "Villaines-sous-Bois",
      "postalCode": "95570",
      "addressCountry": "FR"
    },
    "areaServed": "Île-de-France"
  },
  "description": "Installation de pompes à chaleur air-air et air-eau en Île-de-France. Certifié RGE QualiPAC. Accès MaPrimeRénov'.",
  "serviceType": "Installation Pompe à Chaleur"
};

const PRESTATIONS = [
  { title: 'PAC air-air', desc: 'La plus accessible : extrait les calories de l\'air extérieur pour chauffer ou rafraîchir.' },
  { title: 'PAC air-eau', desc: 'Alimente votre réseau de radiateurs ou plancher chauffant en eau chaude.' },
  { title: 'Système réversible', desc: 'Chauffage en hiver et climatisation en été — deux usages, un seul équipement.' },
  { title: 'Plancher chauffant', desc: 'Couplé à une PAC air-eau pour un confort optimal et une consommation minimale.' },
  { title: 'Radiateurs basse température', desc: 'Compatibles avec les PAC, ils diffusent une chaleur douce et homogène.' },
  { title: 'Conseil sur les aides', desc: 'MaPrimeRénov\', CEE, éco-PTZ : nous vous guidons pour maximiser vos aides.' },
];

const AVANTAGES = [
  { icon: <Leaf size={20} strokeWidth={1.8} />, title: 'Jusqu\'à -70% sur la facture', desc: 'Une PAC produit 3 à 4 kWh de chaleur pour 1 kWh d\'électricité consommé.' },
  { icon: <ShieldCheck size={20} strokeWidth={1.8} />, title: 'Éligible MaPrimeRénov\'', desc: 'Grâce à notre certification RGE, vous accédez à toutes les aides de l\'État.' },
  { icon: <Zap size={20} strokeWidth={1.8} />, title: 'Valorise votre bien', desc: 'Améliorer son DPE avec une PAC augmente la valeur immobilière de votre logement.' },
  { icon: <Clock size={20} strokeWidth={1.8} />, title: 'Durée de vie 15-20 ans', desc: 'Avec un entretien annuel, une pompe à chaleur dure en moyenne 15 à 20 ans.' },
];

const FAQ = [
  { q: 'Quel est le prix d\'une pompe à chaleur en Île-de-France ?', a: 'Une PAC air-air coûte entre 3 000 € et 7 000 €. Une PAC air-eau entre 8 000 € et 15 000 €. Les aides (MaPrimeRénov\', CEE) peuvent couvrir jusqu\'à 50% du montant.' },
  { q: 'Quelle aide pour une pompe à chaleur en 2025 ?', a: 'MaPrimeRénov\' (jusqu\'à 4 000 €), Certificats d\'Économies d\'Énergie (CEE), éco-PTZ (jusqu\'à 50 000 €) et TVA réduite à 5,5 %. Montants variables selon revenus.' },
  { q: 'Une pompe à chaleur peut-elle remplacer ma chaudière ?', a: 'Oui, une PAC air-eau peut totalement remplacer une chaudière en alimentant vos radiateurs et votre eau chaude sanitaire. Nous réalisons un bilan thermique gratuit.' },
  { q: 'Combien de temps dure l\'installation ?', a: 'L\'installation d\'une PAC air-air prend 1 journée. Une PAC air-eau avec modification du réseau hydraulique nécessite 2 à 3 jours.' },
];

export default function PompeAChaleur() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Installation Pompe à Chaleur Île-de-France | SOCLIMATE</title>
        <meta name="description" content="Installation pompe à chaleur air-air et air-eau en Île-de-France. Certifié RGE QualiPAC. Éligible MaPrimeRénov'. Devis gratuit — économisez jusqu'à 70% sur votre facture." />
        <meta name="keywords" content="pompe à chaleur ile de france, installation pac, pac air air, pac air eau, maprimerénov pompe chaleur, installateur pac 95" />
        <link rel="canonical" href="https://www.soclimate.fr/pompe-a-chaleur" />
        <meta property="og:title" content="Installation Pompe à Chaleur Île-de-France | SOCLIMATE" />
        <meta property="og:description" content="Installation PAC air-air et air-eau. Certifié RGE. Éligible MaPrimeRénov'. Devis gratuit." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      </Helmet>

      <Navbar />

      <main className="sp-page">

        <section className="sp-hero sp-hero--red">
          <div className="sp-hero__bg" style={{ backgroundImage: `url(${imgPompe})` }} />
          <div className="sp-hero__veil" />
          <div className="container sp-hero__inner">
            <div className="sp-hero__breadcrumb">
              <Link to="/">Accueil</Link><ChevronRight size={14} />
              <Link to="/solution">Services</Link><ChevronRight size={14} />
              <span>Pompe à chaleur</span>
            </div>
            <div className="sp-hero__badge">
              <AirVent size={16} strokeWidth={2} />
              Air-Air · Air-Eau · MaPrimeRénov'
            </div>
            <h1 className="sp-hero__h1">
              Pompe à Chaleur<br />
              <span>Île-de-France</span>
            </h1>
            <p className="sp-hero__sub">
              Réduisez jusqu'à 70% votre facture de chauffage avec une pompe
              à chaleur installée par nos techniciens certifiés RGE.
              Accès aux aides de l'État garanti.
            </p>
            <div className="sp-hero__ctas">
              <a href="tel:0618011895" className="sp-cta-primary">
                <Phone size={16} strokeWidth={2} /> 06 18 01 18 95
              </a>
              <a href="#contact" className="sp-cta-ghost">
                Devis gratuit <ArrowRight size={15} strokeWidth={2} />
              </a>
            </div>
            <div className="sp-hero__stats">
              <div className="sp-stat"><strong>-70%</strong><span>Sur la facture</span></div>
              <div className="sp-stat__sep" />
              <div className="sp-stat"><strong>RGE</strong><span>Certifié</span></div>
              <div className="sp-stat__sep" />
              <div className="sp-stat"><strong>5★</strong><span>Avis clients</span></div>
              <div className="sp-stat__sep" />
              <div className="sp-stat"><strong>24h</strong><span>Délai devis</span></div>
            </div>
          </div>
        </section>

        <section className="sp-intro">
          <div className="container sp-intro__grid">
            <div className="sp-intro__text">
              <h2>Installation pompe à chaleur en Île-de-France</h2>
              <p>
                La pompe à chaleur est aujourd'hui la solution de chauffage la plus
                économique et écologique. SOCLIMATE installe et entretient vos PAC
                dans tout le <strong>Val-d'Oise (95)</strong> et la région Île-de-France.
              </p>
              <p>
                Notre certification <strong>RGE QualiPAC</strong> est la condition
                indispensable pour que vous puissiez bénéficier de
                <strong> MaPrimeRénov'</strong>, des Certificats d'Économies d'Énergie
                (CEE) et de l'éco-PTZ.
              </p>
              <ul className="sp-check-list">
                <li><CheckCircle2 size={16} strokeWidth={2} />Bilan thermique gratuit</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Aide au montage des dossiers d'aides</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Installation conforme RE2020</li>
                <li><CheckCircle2 size={16} strokeWidth={2} />Entretien annuel disponible</li>
              </ul>
            </div>
            <div className="sp-intro__rge">
              <img src={logoRGE} alt="Certification RGE QualiPAC SOCLIMATE" className="sp-intro__rge-logo" />
              <p>Certification obligatoire pour accéder à <strong>MaPrimeRénov'</strong> et aux aides de l'État</p>
            </div>
          </div>
        </section>

        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />NOS PRESTATIONS PAC</p>
              <h2>Solutions pompe à chaleur<br /><em>adaptées à votre logement</em></h2>
            </div>
            <div className="sp-grid-3">
              {PRESTATIONS.map((p, i) => (
                <div key={i} className="sp-presta-card">
                  <div className="sp-presta-card__num">0{i + 1}</div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />LES AVANTAGES</p>
              <h2>Pourquoi passer <em>à la pompe à chaleur</em></h2>
            </div>
            <div className="sp-grid-2">
              {AVANTAGES.map((a, i) => (
                <div key={i} className="sp-avantage">
                  <div className="sp-avantage__icon">{a.icon}</div>
                  <div><h3>{a.title}</h3><p>{a.desc}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section sp-section--gray">
          <div className="container">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />ZONES D'INTERVENTION</p>
              <h2>Pompe à chaleur<br /><em>dans toute l'Île-de-France</em></h2>
            </div>
            <div className="sp-zones">
              {['Val-d\'Oise (95)', 'Paris (75)', 'Yvelines (78)', 'Essonne (91)', 'Hauts-de-Seine (92)', 'Seine-Saint-Denis (93)', 'Val-de-Marne (94)', 'Seine-et-Marne (77)'].map((z, i) => (
                <div key={i} className="sp-zone-pill"><MapPin size={13} strokeWidth={2} /> {z}</div>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-section">
          <div className="container sp-faq-wrap">
            <div className="sp-head">
              <p className="sp-eyebrow"><span />FAQ</p>
              <h2>Questions fréquentes<br /><em>sur la pompe à chaleur</em></h2>
            </div>
            <div className="sp-faq">
              {FAQ.map((item, i) => (
                <details key={i} className="sp-faq__item">
                  <summary className="sp-faq__q">{item.q}</summary>
                  <p className="sp-faq__a">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-cta-band">
          <div className="container sp-cta-band__inner">
            <div>
              <h2>Passez à la pompe à chaleur en Île-de-France</h2>
              <p>Devis gratuit — Aide au montage MaPrimeRénov' incluse.</p>
            </div>
            <div className="sp-cta-band__btns">
              <a href="tel:0618011895" className="sp-cta-primary"><Phone size={16} strokeWidth={2} /> 06 18 01 18 95</a>
              <a href="mailto:soclimate.95@gmail.com" className="sp-cta-ghost sp-cta-ghost--light"><Mail size={16} strokeWidth={2} /> Email</a>
            </div>
          </div>
        </section>

      </main>
      <Contact />
      <Footer />
    </>
  );
}